import { TFunction } from 'i18next';
import { Language } from '@/services/language.service';
import {
    FieldName,
    FieldType,
    IAPIFormSchema,
    IAPISchemaOptionalProperties,
    ISchemaFieldProperties,
    WidgetName,
} from '@/services/formSchema.service';

const MAX_FILE_SIZE = '10000000';

interface IRegexStrategy {
    [language: string]: () => RegExp;
}
interface IStringStrategy {
    [language: string]: () => string;
}

interface IObjectStrategy {
    [language: string]: () => {
        value: any;
        message: string;
    };
}

export const matchAdditionalPatternValidation = (fieldName: string, t: TFunction, widget?: string) => {
    if (widget === WidgetName.PASSWORD && fieldName.includes('second')) {
        return {
            value: 'plaintextPassword.first',
            message: t('translations.forms.error.additionalPatternPassword'),
        };
    }

    if (widget === FieldType.FILE) {
        return {
            value: MAX_FILE_SIZE,
            message: t('translations.forms.error.maxSizeFile'),
        };
    }

    return;
};

export const matchPatternValidation = (fieldName: string, lang: string, t: TFunction, widget?: string) => {
    if (widget) {
        const widgetStrategy: IObjectStrategy = {
            [WidgetName.EMAIL]: () => {
                return {
                    value: matchEmailRegex(),
                    message: t('translations.forms.error.patternEmail'),
                };
            },
            [WidgetName.PASSWORD]: () => {
                return {
                    value: matchPasswordRegexp(),
                    message: t('translations.forms.error.patternPassword'),
                };
            },
        };

        if (widgetStrategy[widget]) {
            return widgetStrategy[widget]();
        }
    }

    const fieldNameStrategy: IObjectStrategy = {
        [FieldName.PHONE]: () => {
            return {
                value: matchPhoneRegexp(lang),
                message: t('translations.forms.error.patternPhone'),
            };
        },
        [FieldName.FIRST_NAME]: () => {
            return {
                value: matchNameRegexp(lang),
                message: t('translations.forms.error.patternFirstName'),
            };
        },
        [FieldName.SURNAME]: () => {
            return {
                value: matchNameRegexp(lang),
                message: t('translations.forms.error.patternSurname'),
            };
        },
        [FieldName.TITLE]: () => {
            return {
                value: matchProjectTitleRegex(),
                message: t('translations.forms.error.patternTitle'),
            };
        },

        [FieldName.ORIGINAL_DIMENSIONS]: () => {
            return {
                value: matchOriginalDimensionsRegex(),
                message: t('translations.forms.error.patternOriginalDimensions'),
            };
        },
    };

    if (fieldNameStrategy[fieldName]) {
        return fieldNameStrategy[fieldName]();
    }

    return undefined;
};

const matchPhoneRegexp = (language: string): RegExp => {
    const regexStrategy: IRegexStrategy = {
        [Language.pl_PL]: () => /^\+([\d]{11,15})$/,
        [Language.sk_SK]: () => /^\+([\d]{11,15})$/,
        [Language.cs_CZ]: () => /^\+([\d]{11,15})$/,
        [Language.en_GB]: () => /^\+([\d]{11,15})$/,
    };

    if (regexStrategy[language]) {
        return regexStrategy[language]();
    }

    return /^(\+[0-9]{11}\s{0,1})$/;
};

const matchNameRegexp = (language: string): RegExp => {
    const regexStrategy: IRegexStrategy = {
        [Language.pl_PL]: () => /^([a-zA-ZÀ-Ž\&\-\.\,\‘\'\"\`\s])+$/,
        [Language.sk_SK]: () => /^([a-zà-žA-ZÀ-Ž\&\-\.\,\‘\'\"\`\s])+$/,
        [Language.cs_CZ]: () => /^([a-zà-žA-ZÀ-Ž\&\-\.\,\‘\'\"\`\s])+$/,
        [Language.en_GB]: () => /^([a-zA-Zà-žÀ-ŽА-ЪЬЮ-ЯА-Яа-яα-ωΑ-ΩίϊΐόάέύϋΰήώΊΪΌΆΈΎΫΉΏ\&\-\.\'\,\s])+$/,
    };

    if (regexStrategy[language]) {
        return regexStrategy[language]();
    }

    return /^(\+[0-9]{11}\s{0,1})$/;
};

export const matchPrefix = (fieldName: string, prefix?: string[]): string | undefined => {
    if (fieldName === FieldName.PHONE) {
        return prefix?.[0];
    }

    return undefined;
};
const matchPasswordRegexp = () => new RegExp('^(?=.*\\p{Lu}.*).{8,}', 'u');

export const matchEmailRegex = () => /^(?!.*\.\.)[a-zA-ZÀ-Ž0-9_\+\-\.]+@([a-zA-ZÀ-Ž0-9_\+\-\.])+\.([a-zA-ZÀ-Ž]{2,})$/;

const matchProjectTitleRegex = () => /^([a-zA-ZÀ-Ž0-9\-\.\,\'\s])+$/;

export const matchOriginalDimensionsRegex = (): RegExp =>
    /^\s*\d{1,5}(?:,\d{1,2})?\s*cm\s+x\s+\d{1,5}(?:,\d{1,2})?\s*cm\s*$/;
